// Figma: no frame provided for this section yet — built using the established design
// system (stat-card row pattern from ../StatCards.js) per the "extrapolate" decision.
// LOGIC PORTED from LC2's DetailsContent.js (commons/components/HomeTabs/Today/Portfolio/
// DetailsContent.js) — specifically its `openItem === 'communities'` branch: same full
// address join (street/city/state/zip/country), same Property status, same Total units
// (while Onboarding) vs. Total Stake units switch, same "Active Stake+ campaigns" count
// (campaigns scoped to this community, status Active, subtype != 'Stake1'). Simplified:
// LC2 showed a loading spinner per-field while `residentLoadingStatus`/`communityLoadingStatus`
// RTK Query state resolved — dropped here since `entities.communities`/`entities.campaigns`
// are already loaded account-wide by MainLayout.js before this section can render.
import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {Box, Stack, Typography} from '@mui/material';

import {getCampaigns, getCommunities} from '../../../app/store/reducers/entities';
import {getDashboardCommunity} from '../../../app/store/reducers/dashboard';
import {formatNumber} from '../../../commons/constants/stylingConstants';
import {tokens} from '../../../themes/ThemeCustomization';
import Badge from '../components/Badge';

const StatCard = ({label, children, borderLeft, flex = 1}) => (
    <Stack
        sx={{
            flex,
            minWidth: '166px',
            p: 2,
            gap: 0.5,
            backgroundColor: tokens.color.white,
            borderLeft: borderLeft ? `1px solid ${tokens.color.neutral200}` : 'none'
        }}
    >
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        {children}
    </Stack>
);

const CommunityStatCards = () => {
    const communities = useSelector(getCommunities);
    const campaigns = useSelector(getCampaigns);
    const dashboardCommunity = useSelector(getDashboardCommunity);

    const stats = useMemo(() => {
        const community = communities.find((c) => c.sfid === dashboardCommunity.sfid);
        if (!community) return null;

        const addressParts = [
            community.full_address__street__s,
            community.full_address__city__s,
            community.full_address__statecode__s,
            community.full_address__postalcode__s,
            community.full_address__countrycode__s
        ].filter(Boolean);
        const fullAddress = addressParts.join(', ') || '-';

        const totalUnits = community.total_units__c || 0;
        const isOnboarding = community.property_status__c === 'Onboarding';
        const unitsLabel = isOnboarding ? 'Total units' : 'Total Stake units';
        const unitsValue = totalUnits > 0 ? formatNumber(totalUnits) : isOnboarding ? 'Processing' : 'No units';

        const communityCampaigns = campaigns.filter((campaign) => campaign.property_sfid === dashboardCommunity.sfid);
        const activeStakePlusCampaigns = communityCampaigns.filter(
            (campaign) => campaign.status === 'Active' && campaign.subtype !== 'Stake1'
        );

        return {
            address: fullAddress,
            propertyStatus: community.property_status__c || 'Unknown',
            unitsLabel,
            unitsValue,
            activeStakePlusCount: activeStakePlusCampaigns.length
        };
    }, [communities, campaigns, dashboardCommunity]);

    if (!stats) return null;

    return (
        <Box sx={{display: 'flex', border: `1px solid ${tokens.color.neutral200}`, borderRadius: '6px', overflow: 'hidden'}}>
            <StatCard label="Address" flex={1.5}>
                <Typography variant="subtitle1" fontWeight={600} noWrap title={stats.address}>
                    {stats.address}
                </Typography>
            </StatCard>
            <StatCard label="Property status" borderLeft>
                <Box>
                    <Badge label={stats.propertyStatus} variant={stats.propertyStatus === 'Onboarding' ? 'info' : 'success'} />
                </Box>
            </StatCard>
            <StatCard label={stats.unitsLabel} borderLeft>
                <Typography variant="subtitle1" fontWeight={600}>
                    {stats.unitsValue}
                </Typography>
            </StatCard>
            <StatCard label="Active Stake+ campaigns" borderLeft>
                <Typography variant="subtitle1" fontWeight={600}>
                    {formatNumber(stats.activeStakePlusCount)}
                </Typography>
            </StatCard>
        </Box>
    );
};

export default CommunityStatCards;
